/**
 * Big HUD moments and overlays: the countdown (with what the race is about), the
 * level-complete banner, the banner of a whole ball or wave level, the finish card, «Время
 * вышло!» of a race against the clock, the in-game menu, the spectator bar, the "rotate your
 * phone" hint, opponent notices and the names on split-screen views.
 */
import { avatarEmoji } from '@cube-rush/shared';
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ru } from '../../i18n/ru';
import { CONTROL_ATTR } from '../../input/GameInput';
import { Button } from '../../ui/components/Button';
import { formatRaceTimer } from '../raceClock';
import type { HudActions } from './actions';
import type { HudDom } from './hudDom';
import type { HudModel } from './hudModel';

const control = { [CONTROL_ATTR]: '' };

/** After this long the finish card moves out of the way. */
const FINISH_COMPACT_MS = 3500;

/**
 * A layer over the main view: the whole screen, or the big view of a split screen, so the
 * countdown, the level banner and the finish card sit on the racer's own view and not across
 * the seam between views.
 */
export function MainViewLayer({ hud, children }: { hud: HudModel; children: ComponentChildren }) {
  const rect = hud.mainRect.value;
  const style =
    rect.w > 0
      ? { top: `${rect.y}px`, left: `${rect.x}px`, width: `${rect.w}px`, height: `${rect.h}px` }
      : undefined;
  return (
    <div class="hud-main" style={style}>
      {children}
    </div>
  );
}

/** What the race is about: «Кто больше уровней за 5 минут?», «Пройди 10 уровней быстрее всех!». */
export function raceGoal(levelCount: number, durationMs: number | null): string {
  if (durationMs === null) return ru.game.goalLevels(ru.race.levels(levelCount));
  const seconds = Math.round(durationMs / 1000);
  const length =
    seconds % 60 === 0 ? ru.game.goalMinutes(seconds / 60) : ru.game.goalSeconds(seconds);
  return ru.game.goalTime(length);
}

export function Countdown({ hud, touch }: { hud: HudModel; touch: boolean }) {
  const value = hud.countdown.value;
  if (value === null) return null;
  const go = value === 0;
  const text = go ? ru.game.go : value > 3 ? ru.game.getReady : String(value);
  // What the race is about, for the racers («Пройди…» is not for those who watch).
  const goal = !go && !hud.spectator;
  return (
    <div class={`hud-countdown${goal ? ' hud-countdown--goal' : ''}`} aria-live="assertive">
      {goal && (
        <span class="hud-countdown__goal title-text">
          {raceGoal(hud.levelCount, hud.durationMs)}
        </span>
      )}
      <span
        key={text}
        class={`hud-countdown__value title-text${go ? ' hud-countdown__value--go' : ''}${value > 3 ? ' hud-countdown__value--ready' : ''}`}
      >
        {text}
      </span>
      {!go && !hud.spectator && <span class="hud-countdown__hint">{controlsHint(hud, touch)}</span>}
    </div>
  );
}

/** How to jump and use boosts: with the pad in use, the touch screen or the keyboard. */
function controlsHint(hud: HudModel, touch: boolean): string {
  const pad = hud.padStyle.value;
  if (pad) return ru.gamepad.hint[pad];
  return touch ? ru.game.tapToJump : ru.game.keysHint;
}

export function LevelFlash({ hud }: { hud: HudModel }) {
  const flash = hud.levelFlash.value;
  if (!flash) return null;
  return (
    <div class="hud-flash" role="status">
      <span key={flash.key} class="hud-flash__text title-text">
        {ru.game.levelComplete(flash.level)}
      </span>
    </div>
  );
}

/**
 * «Шарик!» / «Стрелочка!» / «Робот!» / «Паук!» as a whole level starts, under the level-complete
 * banner, and «Робот!» / «Паук!» at their portal in a cube level; the first time in the race also
 * how to play it, with the pad, keys or touch — or, as a badge, the title alone along the top bar.
 */
export function ModeBanner({ hud, touch }: { hud: HudModel; touch: boolean }) {
  const intro = hud.modeIntro.value;
  if (!intro) return null;
  const pad = hud.padStyle.value;
  let hint: string;
  if (intro.mode === 'boss') hint = ru.game.bossHint[intro.boss ?? 'dragon'];
  else {
    const hints = ru.game.modeHint[intro.mode];
    hint = pad ? hints.pad(ru.gamepad.jumpButton[pad]) : touch ? hints.touch : hints.keys;
  }
  const boss = intro.mode === 'boss' ? ` hud-mode--boss-${intro.boss ?? 'dragon'}` : '';
  const badge = intro.badge ? ' hud-mode--badge' : '';
  return (
    <div class={`hud-mode hud-mode--${intro.mode}${boss}${badge}`} role="status">
      <div key={intro.key} class={`hud-mode__card${intro.hint ? ' hud-mode__card--hint' : ''}`}>
        <span class="hud-mode__title title-text">{ru.game.modeTitle[intro.mode]}</span>
        {intro.hint && <span class="hud-mode__hint">{hint}</span>}
      </div>
    </div>
  );
}

function medal(place: number): string {
  return place === 1 ? '🥇' : place === 2 ? '🥈' : place === 3 ? '🥉' : '🏅';
}

export function FinishCard({ hud, dom }: { hud: HudModel; dom: HudDom }) {
  const finish = hud.finish.value;
  const [compact, setCompact] = useState(false);
  const shown = finish !== null;
  useEffect(() => {
    if (!shown) return;
    const timer = setTimeout(() => setCompact(true), FINISH_COMPACT_MS);
    return () => clearTimeout(timer);
  }, [shown]);
  if (!finish) return null;
  return (
    <div class={`hud-finish${compact ? ' hud-finish--compact' : ''}`} role="status">
      {/* Compact, it sits along the bottom edge: the views keep the floor above it. */}
      <div
        class="hud-finish__card panel"
        ref={(element) => dom.bottomControl('finish', compact ? element : null)}
      >
        <span class="hud-finish__title title-text">{ru.game.finish}</span>
        <span class="hud-finish__time">
          <span class="hud-finish__label">{ru.game.yourTime}</span> {formatRaceTimer(finish.timeMs)}
        </span>
        {finish.place !== null && (
          <span class="hud-finish__place">
            <span aria-hidden="true">{medal(finish.place)}</span> {ru.game.place(finish.place)}
          </span>
        )}
        <span class="hud-finish__wait">
          {finish.waiting ? ru.game.waiting : ru.game.allFinished}
        </span>
      </div>
    </div>
  );
}

/**
 * The end of a race against the clock, where the finish card would be: «Время вышло!», the
 * levels done and, once the server has them all, the place. Spectators get the title only.
 */
export function TimeUpCard({ hud, dom }: { hud: HudModel; dom: HudDom }) {
  const timeUp = hud.timeUp.value;
  const [compact, setCompact] = useState(false);
  const shown = timeUp !== null;
  useEffect(() => {
    if (!shown) return;
    const timer = setTimeout(() => setCompact(true), FINISH_COMPACT_MS);
    return () => clearTimeout(timer);
  }, [shown]);
  if (!timeUp) return null;
  return (
    <div class={`hud-timeup${compact ? ' hud-timeup--compact' : ''}`} role="status">
      {/* Compact, it sits along the bottom edge: the views keep the floor above it. */}
      <div
        class="hud-timeup__card panel"
        ref={(element) => dom.bottomControl('timeUp', compact ? element : null)}
      >
        <span class="hud-timeup__title title-text">{ru.game.timeUp}</span>
        {timeUp.levels !== null && (
          <span class="hud-timeup__levels">{ru.game.levelsDone(timeUp.levels)}</span>
        )}
        {timeUp.place !== null && (
          <span class="hud-timeup__place">
            <span aria-hidden="true">{medal(timeUp.place)}</span> {ru.game.place(timeUp.place)}
          </span>
        )}
        <span class="hud-timeup__wait">{ru.game.waitingResults}</span>
      </div>
    </div>
  );
}

export function GameMenu({ hud, actions }: { hud: HudModel; actions: HudActions }) {
  if (!hud.menuOpen.value) return null;
  return <MenuPanel hud={hud} actions={actions} />;
}

/** The open menu; the race goes on behind it. Focus starts on "Продолжить" (keyboard users). */
function MenuPanel({ hud, actions }: { hud: HudModel; actions: HudActions }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const confirm = hud.confirmQuit.value;
  const phase = hud.phase.value;
  const racing = !hud.spectator && phase !== 'finished' && phase !== 'timeUp';
  // Against the clock stopping keeps the levels done: «Закончить», not «Сдаться».
  const quit = hud.timed
    ? { label: ru.game.stop, question: ru.game.stopConfirm, icon: '⏹️' }
    : { label: ru.game.giveUp, question: ru.game.giveUpConfirm, icon: '🏳️' };
  useEffect(() => {
    panelRef.current?.querySelector('button')?.focus({ preventScroll: true });
  }, []);
  return (
    <div class="hud-menu" {...control}>
      <button
        type="button"
        class="hud-menu__backdrop"
        tabIndex={-1}
        aria-label={ru.game.resume}
        onClick={() => actions.setMenu(false)}
      />
      <div
        ref={panelRef}
        class="hud-menu__panel panel"
        role="dialog"
        aria-modal="true"
        aria-label={ru.game.menu}
      >
        <h2 class="hud-menu__title title-text">{ru.game.menu}</h2>
        {racing && <p class="hud-menu__note">{ru.game.menuNote}</p>}
        <Button variant="green" size="big" block icon="▶" onClick={() => actions.setMenu(false)}>
          {ru.game.resume}
        </Button>
        <Button variant="blue" block icon="⚙️" onClick={() => actions.openSettings()}>
          {ru.game.settings}
        </Button>
        <Button
          variant="red"
          block
          icon={racing ? quit.icon : '🚪'}
          onClick={() => actions.requestQuit()}
        >
          {!racing ? (
            ru.game.leave
          ) : confirm ? (
            // Two short lines next to the flag instead of one long line that wraps anywhere.
            <span class="hud-menu__confirm">
              {quit.question} <small>{ru.game.giveUpAgain}</small>
            </span>
          ) : (
            quit.label
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Over the game while a phone is held upright (the top bar and the menu stay above it). Says
 * where the rotation lock is, for phones that do not turn.
 */
export function RotateHint({ ios }: { ios: boolean }) {
  const unlock = ios ? ru.game.rotateUnlock.ios : ru.game.rotateUnlock.android;
  return (
    <div class="hud-rotate" {...control} role="alert">
      <span class="hud-rotate__phone" aria-hidden="true">
        📱
      </span>
      <span class="hud-rotate__title title-text">{ru.game.rotateTitle}</span>
      <span class="hud-rotate__text">{ru.game.rotateText}</span>
      <span class="hud-rotate__locked">
        <b>{ru.game.rotateLocked}</b> {unlock}
      </span>
    </div>
  );
}

export function SpectatorBar({
  hud,
  dom,
  actions,
  nameOf,
}: {
  hud: HudModel;
  dom: HudDom;
  actions: HudActions;
  nameOf: (id: string) => { name: string; avatar: string };
}) {
  const phase = hud.phase.value;
  if (phase !== 'spectating') return null;
  const watching = hud.watching.value;
  const canSwitch = hud.canSwitch.value;
  const who = watching ? nameOf(watching) : null;
  return (
    <div
      class="hud-spectator panel"
      {...control}
      ref={(element) => dom.bottomControl('spectator', element)}
    >
      <span class="hud-spectator__eye" aria-hidden="true">
        👀
      </span>
      <span class="hud-spectator__text">
        {who ? ru.game.watchingName(`${who.avatar} ${who.name}`) : ru.game.watching}
      </span>
      {canSwitch && (
        <>
          <button
            type="button"
            class="hud-icon-btn"
            aria-label={ru.game.previous}
            onClick={() => actions.switchRacer(-1)}
          >
            ◀
          </button>
          <button
            type="button"
            class="hud-icon-btn"
            aria-label={ru.game.next}
            onClick={() => actions.switchRacer(1)}
          >
            ▶
          </button>
        </>
      )}
      <Button variant="red" size="small" onClick={() => actions.requestQuit()}>
        {ru.game.leave}
      </Button>
    </div>
  );
}

export function Notices({ hud }: { hud: HudModel }) {
  const notices = hud.notices.value;
  if (notices.length === 0) return null;
  return (
    <div class="hud-notices" aria-live="polite">
      {notices.map((notice) => (
        <div key={notice.key} class="hud-notice">
          <span aria-hidden="true">{avatarEmoji(notice.avatar)}</span>
          <b>{notice.name}:</b> {notice.text}
        </div>
      ))}
    </div>
  );
}

/** Names on the views that follow other racers (split screen). */
export function ViewLabels({ hud, topInset }: { hud: HudModel; topInset: number }) {
  const labels = hud.labels.value;
  if (labels.length === 0) return null;
  return (
    <>
      {labels.map((label) => {
        // Views at the top edge keep their name below the top bar; views at a screen edge
        // keep it clear of notches.
        const atTop = label.rect.y === 0;
        const atLeft = label.rect.x === 0;
        return (
          <div
            key={label.key}
            class={`hud-view-label${label.stale ? ' hud-view-label--stale' : ''}`}
            style={{
              top: atTop ? `calc(var(--safe-top) + ${topInset}px)` : `${label.rect.y + 8}px`,
              left: atLeft
                ? `calc(var(--safe-left) + ${label.rect.x + 8}px)`
                : `${label.rect.x + 8}px`,
            }}
          >
            <span aria-hidden="true">{avatarEmoji(label.avatar)}</span>
            <b>{label.name}</b>
            <span class="hud-view-label__level">{ru.game.levelShort(label.level)}</span>
            {label.finished ? (
              <span class="hud-view-label__finished">{ru.game.finishedLabel}</span>
            ) : (
              label.stale && <span class="hud-view-label__offline">{ru.game.offline}</span>
            )}
          </div>
        );
      })}
    </>
  );
}
